import { MetadataClient } from "@formant/core/client/MetadataClient";
import { combineTags } from "@formant/core/common/combineTags";
import { defined } from "@formant/core/common/defined";
import { distinct } from "@formant/core/common/distinct";
import { duration } from "@formant/core/model/duration";
import { IMetadata } from "@formant/core/model/IMetadata";
import { IMetadataWithCurrentValue } from "@formant/core/model/IMetadataWithCurrentValue";
import { IScopeFilter } from "@formant/core/model/IScopeFilter";
import { ITagSets } from "@formant/core/model/ITagSets";
import { StreamType } from "@formant/core/model/StreamType";
import { streamTypes } from "@formant/core/model/streamTypes";
import { Uuid } from "@formant/core/model/Uuid";

import { IAuthStore } from "./IAuthStore";
import { IDeviceDetailsStore } from "./IDeviceDetailsStore";
import { IMetadataStore } from "./IMetadataStore";
import { IStoreCacheOptions, StoreCache } from "./StoreCache";

const cacheOptions: IStoreCacheOptions = {
    staleIntervalMs: 30 * duration.second,
};

export class MetadataStore implements IMetadataStore {
    private listCache = new StoreCache<IScopeFilter, IMetadata[]>(cacheOptions);
    private streamNamesCache = new StoreCache<IScopeFilter, string[]>(
        cacheOptions,
    );
    private streamTypesCache = new StoreCache<IScopeFilter, StreamType[]>(
        cacheOptions,
    );
    private deviceIdsCache = new StoreCache<IScopeFilter, Uuid[]>(cacheOptions);
    private tagOptionsCache = new StoreCache<IScopeFilter, ITagSets>(
        cacheOptions,
    );

    constructor(
        private metadataClient: MetadataClient,
        private authStore: IAuthStore,
        private deviceDetailsStore: IDeviceDetailsStore,
    ) {}

    public list(filter: IScopeFilter): IMetadata[] | undefined {
        return this.listCache.get(
            filter,
            async () =>
                await this.metadataClient.list(
                    await this.authStore.token(),
                    filter,
                ),
        );
    }

    public async listAsync(filter: IScopeFilter): Promise<IMetadata[]> {
        return await this.metadataClient.list(
            await this.authStore.token(),
            filter,
        );
    }

    public async listMetadataWithCurrentValueAsync(
        filter: IScopeFilter,
    ): Promise<IMetadataWithCurrentValue[]> {
        return await this.metadataClient.listMetadataWithCurrentValue(
            await this.authStore.token(),
            filter,
        );
    }

    public async getLatestValueForMetadataIdAsync(
        metadataId: string,
        rangeDays?: number,
    ): Promise<IMetadataWithCurrentValue | undefined> {
        return await this.metadataClient.getLatestValueForMetadataId(
            await this.authStore.token(),
            metadataId,
            rangeDays,
        );
    }

    public async listStreamsAsync(
        filter: IScopeFilter,
    ): Promise<{ [streamName: string]: StreamType }> {
        const metadata = (await this.listAsync(filter)) || [];

        const result: { [streamName: string]: StreamType } = {};
        metadata.forEach(m => {
            const currentType = result[m.name];
            if (currentType !== undefined) {
                if (
                    streamTypes.indexOf(m.type) <
                    streamTypes.indexOf(currentType)
                ) {
                    result[m.name] = m.type;
                }
            } else {
                result[m.name] = m.type;
            }
        });
        return result;
    }

    public listStreams(
        filter: IScopeFilter,
    ): { [streamName: string]: StreamType } | undefined {
        const metadata = this.list(filter);
        if (!metadata) {
            return undefined;
        }
        const result: { [streamName: string]: StreamType } = {};
        metadata.forEach(m => {
            const currentType = result[m.name];
            if (currentType !== undefined) {
                if (
                    streamTypes.indexOf(m.type) <
                    streamTypes.indexOf(currentType)
                ) {
                    result[m.name] = m.type;
                }
            } else {
                result[m.name] = m.type;
            }
        });
        return result;
    }

    public listStreamNames(filter: IScopeFilter): string[] | undefined {
        return this.streamNamesCache.get(filter, () =>
            this.fetchStreamNames(filter),
        );
    }

    public listStreamTypes(filter: IScopeFilter): StreamType[] | undefined {
        const metadata = this.list(filter);
        if (!metadata) {
            return undefined;
        }
        return this.streamTypesCache.get(filter, async () => {
            return distinct(metadata.map(_ => _.type));
        });
    }

    public listDeviceIds(filter: IScopeFilter): Uuid[] | undefined {
        return this.deviceIdsCache.get(
            filter,
            async () =>
                await this.metadataClient.listDeviceIds(
                    await this.authStore.token(),
                    filter,
                ),
        );
    }

    public listValidDeviceIds(filter: IScopeFilter): Uuid[] | undefined {
        const enabledDevices = this.deviceDetailsStore.list({
            enabled: true,
            type: "default",
        });
        if (enabledDevices === undefined) {
            return undefined;
        }
        const deviceIds = this.listDeviceIds(filter);
        if (deviceIds === undefined) {
            return undefined;
        }
        const enabledIds = new Set(enabledDevices.map(_ => defined(_.id)));
        return deviceIds.filter(_ => enabledIds.has(_));
    }

    public tagOptions(filter: IScopeFilter): ITagSets | undefined {
        const metadata = this.list(filter);

        if (metadata === undefined) {
            return undefined;
        }

        return this.tagOptionsCache.get(filter, async () => {
            return combineTags(metadata.map(_ => _.tags));
        });
    }

    public async tagOptionsAsync(filter: IScopeFilter): Promise<ITagSets> {
        const metadata = await this.listAsync(filter);
        return combineTags(metadata.map(_ => _.tags));
    }

    public async fetchStreamNames(filter: IScopeFilter) {
        const results = await this.metadataClient.listStreamNames(
            await this.authStore.token(),
            filter,
        );
        this.streamNamesCache.set(filter, results);
        return results;
    }
}
